/**
 * ProgressBar — a thin track with a filled portion.
 *
 * The student area wrote this by hand seven times as
 * `<div className="bar"><div className="bar__fill" style={{width}}/></div>`,
 * plus once more under a different name (`.student-gameplay__answer-strength`)
 * with the same declarations.
 *
 * `value` is 0-100. It is clamped, because a percentage computed from live
 * counts can exceed 100 when a total is stale, and a fill wider than its track
 * looks like a rendering bug rather than a data one.
 */

import styles from './progress-bar.module.css';

interface ProgressBarProps {
  /** 0-100. */
  value: number;
  /** Gold fill — the completed treatment. */
  gold?: boolean;
  /**
   * Minimum visible width, as a percentage, when `value` is above zero. A
   * one-percent fill rounds to nothing and reads as "not started".
   */
  minVisible?: number;
  /**
   * Accessible name. Without one the bar is treated as decoration and hidden
   * from assistive tech — which is right when the value is already in text
   * beside it ("0 / 530 XP"), and wrong to leave as an unnamed progressbar
   * role, which axe reports as a serious violation.
   */
  label?: string;
  /** Take the leftover width of a flex row, or the full width of a block. */
  grow?: boolean;
  className?: string;
}

export function ProgressBar({ value, gold, minVisible = 0, label, grow, className }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const width = clamped > 0 ? Math.max(clamped, minVisible) : 0;

  return (
    <div
      className={[styles.track, gold ? styles.gold : null, grow ? styles.grow : null, className]
        .filter(Boolean)
        .join(' ')}
      {...(label
        ? {
            role: 'progressbar' as const,
            'aria-valuenow': Math.round(clamped),
            'aria-valuemin': 0,
            'aria-valuemax': 100,
            'aria-label': label,
          }
        : { 'aria-hidden': true })}
    >
      <div className={styles.fill} style={{ width: `${width}%` }} />
    </div>
  );
}
